import * as Sentry from '@sentry/react'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'

type Props = {
  // Changing this clears a previous error so a newly selected document can render
  resetKey: number | string | null
  fallback?: ReactNode
  children: ReactNode
}

type State = {
  hasError: boolean
}

const DEFAULT_FALLBACK = <div>Failed to load document</div>

// Keeps Syncfusion viewer failures (render or teardown) inside the labs preview
// instead of letting the global boundary redirect the whole app to /error/500.
export class PdfViewerErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo)
    Sentry.captureException(error, {
      tags: { feature: 'labs-pdf-viewer' },
      extra: { componentStack: errorInfo.componentStack, resetKey: this.props.resetKey },
    })
  }

  render() {
    const { children, fallback = DEFAULT_FALLBACK } = this.props
    return this.state.hasError ? fallback : children
  }
}
